import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Checkbox, Chip, Divider, IconButton, ListItemIcon, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useCadastrarEditarProdutoContext } from "../../contexts/CadastrarEditarProdutoContext";
import { IMassa } from "../../interfaces/IMassa";
import { ButtonsContainerForm, ConfiguracaoBordas, FiltroContainer, TabelaVazia } from "./styles";
import { useTamanhoTela } from '../../../core/hooks/useTamanhoTela';
import { FormularioMassa } from './containers/FormularioMassa';
import { ArrowDownward, ArrowDropDown, ArrowUpward, Kitchen, PowerSettingsNew } from '@mui/icons-material';
import { useProdutos } from '../../hooks/useProdutos';
import { ConfirmacaoAcaoModal } from '../../../core/components/ConfirmacaoAcaoModal';
import { IProduto } from '../../interfaces/IProduto';
import { MenuOrdenacao } from '../../components/MenuOrdenacao';

type Tabs = "tabela" | "formulario";

interface ConfiguracaoFormularioContainerProps { }

export const ConfiguracaoMassasContainer = ({ }: ConfiguracaoFormularioContainerProps) => {
    const [filtroMassas, setFiltroMassas] = useState("");
    const { massas, adicionarMassa, excluirMassa, editarMassa, ordenarMassas, trocarPosicaoMassa, handleProximo, handleVoltar, produto, categoriaUuid } = useCadastrarEditarProdutoContext();
    const [tab, setTab] = useState<Tabs>("tabela");
    const [massaEdicao, setMassaEdicao] = useState<IMassa | undefined>(undefined);
    const [massasSelecionadas, setMassasSelecionadas] = useState<string[]>([]);
    const [confirmacaoExclusao, setConfirmacaoExclusao] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { real } = useMoeda();
    const { modeloTela } = useTamanhoTela();
    const { salvar } = useProdutos();

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const limparMassasSelecionadas = () => setMassasSelecionadas([]);

    const toggleSelecionado = (uuid: string) => {
        setMassasSelecionadas(prev =>
            prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
        );
    };

    const toggleSelecionarTodos = () => {
        if (massasSelecionadas.length === massas.length) {
            limparMassasSelecionadas();
        } else {
            setMassasSelecionadas(massas.map(m => m.uuid));
        }
    };

    const handleTab = (tab: Tabs) => {
        setTab(tab);
        setMassaEdicao(undefined);
        limparMassasSelecionadas();
    };

    const handleAbrirEdicao = (massa: IMassa) => {
        setTab("formulario");
        setMassaEdicao(massa);
        handleClose();
    };

    const handleAbrirExclusao = () => {
        setConfirmacaoExclusao(true);
        handleClose();
    };

    const handleFecharExclusao = () => setConfirmacaoExclusao(false);

    const handleExcluirMassas = () => {
        massasSelecionadas.forEach(uuid => {
            const massa = massas.find(m => m.uuid === uuid);
            if (massa) excluirMassa(massa);
        });
        handleFecharExclusao();
        limparMassasSelecionadas();
    };

    const handleToggleAtivacao = async () => {
        if (!produto || !categoriaUuid || massasSelecionadas.length !== 1) return;
        const massa = massas.find(m => m.uuid === massasSelecionadas[0]);
        if (!massa) return;
        const massaAtualizada: IMassa = { ...massa, inativadoAs: massa.inativadoAs ? undefined : new Date() };
        editarMassa(massaAtualizada);
        await salvar(categoriaUuid, {
            ...produto,
            massas: massas.map(m => m.uuid === massa.uuid ? massaAtualizada : m)
        } as IProduto);
        handleClose();
    };

    const massaSelecionada = massasSelecionadas.length === 1 ? massas.find(m => m.uuid === massasSelecionadas[0]) : undefined;

    /** Posição no array que vai ser salvo — a linha pode vir da lista filtrada. */
    const indiceOrdem = (uuid: string) => massas.findIndex(m => m.uuid === uuid);

    const renderizarLinha = (massa: IMassa) => (
        <TableRow key={massa.uuid} sx={{ opacity: massa.inativadoAs ? 0.5 : 1 }}>
            <TableCell align="center">
                <Checkbox
                    checked={massasSelecionadas.includes(massa.uuid)}
                    onChange={() => toggleSelecionado(massa.uuid)}
                />
            </TableCell>
            <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                <Tooltip title={filtroMassas ? "Limpe o filtro para reordenar" : "Mover para cima"}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={!!filtroMassas || indiceOrdem(massa.uuid) <= 0}
                            onClick={() => trocarPosicaoMassa(massa.uuid, "cima")}
                        >
                            <ArrowUpward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={filtroMassas ? "Limpe o filtro para reordenar" : "Mover para baixo"}>
                    <span>
                        <IconButton
                            size="small"
                            disabled={!!filtroMassas || indiceOrdem(massa.uuid) === massas.length - 1}
                            onClick={() => trocarPosicaoMassa(massa.uuid, "baixo")}
                        >
                            <ArrowDownward fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </TableCell>
            <TableCell align="center">
                <img alt={massa.descricao} src={
                    massa?.imagens?.length
                        ? massa?.imagens[0] instanceof File ? URL.createObjectURL(massa?.imagens[0]) : massa?.imagens[0].url
                        : `${process.env.PUBLIC_URL}/images/produtos/generico.png`
                } style={{ width: "80px", height: "70px", borderRadius: "5px" }} />
            </TableCell>
            <TableCell align="center">{massa.descricao}</TableCell>
            <TableCell align="center">{real(massa.preco)}</TableCell>
            <TableCell align="center">
                <Tooltip title={massa.processosPreparoKDS?.length ? `${massa.processosPreparoKDS.length} processo(s) KDS` : 'Sem KDS configurado'}>
                    <Kitchen fontSize="small" color={massa.processosPreparoKDS?.length ? 'primary' : 'disabled'} />
                </Tooltip>
            </TableCell>
            <TableCell align="center">
                <Chip
                    label={massa.inativadoAs ? "Inativo" : "Ativo"}
                    color={massa.inativadoAs ? "default" : "success"}
                    size="small"
                />
            </TableCell>
        </TableRow>
    );

    return (
        <ConfiguracaoBordas>
            {tab == "tabela" && (
                <TableContainer variant='outlined' component={Paper} sx={{ width: "100%", maxHeight: 450, height: "100%", minHeight: 350 }}>
                    <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="h6">Massas</Typography>
                        <FiltroContainer>
                            {modeloTela == "desktop" && (
                                <TextField
                                    placeholder='Nome'
                                    size='small'
                                    value={filtroMassas}
                                    onChange={(e) => setFiltroMassas(e.target.value)}
                                    sx={{ width: "200px" }}
                                />
                            )}
                            <Box>
                                <Button
                                    onClick={handleClick}
                                    endIcon={<ArrowDropDown />}
                                    variant="contained"
                                    disabled={massasSelecionadas.length <= 0}
                                >
                                    Ações
                                </Button>
                                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                                    <MenuItem disabled>
                                        <Typography variant="inherit" noWrap>
                                            {massasSelecionadas.length} {massasSelecionadas.length === 1 ? "massa selecionada" : "massas selecionadas"}
                                        </Typography>
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem disabled={massasSelecionadas.length !== 1} onClick={() => {
                                        const massa = massas.find(m => m.uuid === massasSelecionadas[0]);
                                        if (massa) handleAbrirEdicao(massa);
                                    }}>
                                        <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Editar</Typography>
                                    </MenuItem>
                                    <MenuItem disabled={massasSelecionadas.length !== 1} onClick={handleToggleAtivacao}>
                                        <ListItemIcon><PowerSettingsNew fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>
                                            {massaSelecionada?.inativadoAs ? "Ativar" : "Inativar"}
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem onClick={handleAbrirExclusao} disabled={massasSelecionadas.length === 0}>
                                        <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                                        <Typography variant="inherit" noWrap>Excluir</Typography>
                                    </MenuItem>
                                </Menu>
                            </Box>
                            <MenuOrdenacao
                                titulo="Ordenar massas"
                                desabilitado={massas.length <= 1}
                                ordenar={(direcao) => {
                                    ordenarMassas(direcao);
                                    limparMassasSelecionadas();
                                }}
                            />
                            <Tooltip title='Crie uma nova massa'>
                                <IconButton color="primary" onClick={() => handleTab("formulario")}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </FiltroContainer>
                    </Toolbar>
                    {massas.length ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={massasSelecionadas.length === massas.length && massas.length > 0}
                                            indeterminate={massasSelecionadas.length > 0 && massasSelecionadas.length < massas.length}
                                            onChange={toggleSelecionarTodos}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Define a ordem em que aparece no cardápio, no PDV e no chat. Salve o produto para gravar.">
                                            <span>Ordem</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">Imagem</TableCell>
                                    <TableCell align="center">Nome</TableCell>
                                    <TableCell align="center">Preço</TableCell>
                                    <TableCell align="center">KDS</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtroMassas
                                    ? massas.filter(m => m.descricao.toLowerCase().includes(filtroMassas.toLowerCase())).map(renderizarLinha)
                                    : massas.map(renderizarLinha)}
                            </TableBody>
                        </Table>
                    ) : (
                        <TabelaVazia>Nenhuma massa cadastrada</TabelaVazia>
                    )}
                </TableContainer>
            )}
            {tab == "formulario" && <FormularioMassa
                adicionarMassa={adicionarMassa}
                editarMassa={editarMassa}
                handleTab={handleTab}
                massaEdicao={massaEdicao}
            />}
            {tab == "tabela" && (
                <ButtonsContainerForm>
                    <Button size="medium" variant="outlined" onClick={handleVoltar}>Voltar</Button>
                    <Button size="medium" variant="contained" onClick={handleProximo}>Próximo</Button>
                </ButtonsContainerForm>
            )}
            <ConfirmacaoAcaoModal
                open={confirmacaoExclusao}
                descricao={massasSelecionadas.length === 1 ? "Você deseja excluir a massa selecionada?" : `Você deseja excluir as ${massasSelecionadas.length} massas selecionadas?`}
                titulo={massasSelecionadas.length === 1 ? "Excluir massa" : "Excluir massas"}
                onCancelar={handleFecharExclusao}
                onConfirmar={handleExcluirMassas}
            />
        </ConfiguracaoBordas>
    );
}
